import { nettoyerHtml } from '../utils/nettoyerHtml';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import CertificateFOAD from '../components/CertificateFOAD';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  BookOpen, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  Play, FileText, HelpCircle, Eye, EyeOff, Save, ArrowLeft,
  Youtube, Upload, CheckCircle, XCircle, Award, Settings, X, Download,
  Image, FolderOpen, Calendar,
  Users, UserPlus, UserMinus, Maximize, Minimize, Presentation,
  User, BarChart3, Trophy, Lock, Unlock, ToggleLeft, ToggleRight,
  Shuffle, FileQuestion, PenLine, Database, Gamepad2, Zap,
  RotateCcw, Archive, Clock, AlertTriangle
} from 'lucide-react';

// ============= RICH TEXT EDITOR =============
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
    },
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image', 'blockquote', 'code-block'
  ];

  return (
    <div className="rich-editor-wrapper">
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Redigez le contenu...'}
      />
      <style>{`
        .rich-editor-wrapper .ql-container { min-height: 200px; font-size: 14px; }
        .rich-editor-wrapper .ql-editor { min-height: 200px; }
        .rich-editor-wrapper .ql-toolbar { background: #f8fafc; border-color: #e2e8f0 !important; border-radius: 8px 8px 0 0; }
        .rich-editor-wrapper .ql-container { border-color: #e2e8f0 !important; border-radius: 0 0 8px 8px; }
        .rich-editor-wrapper .ql-editor img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
        .rich-editor-wrapper .ql-editor a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
  );
};

// ============= MOODLE IMPORT MODAL =============
const MoodleImportModal = ({ onClose, onImported }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith('.mbz')) {
      setError('Veuillez selectionner un fichier .mbz (sauvegarde Moodle)');
      return;
    }
    setUploading(true); setError(null);
    try {
      // Upload in chunks to bypass proxy size limits
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('filename', file.name);
        await axios.post('/foad/upload-mbz-chunk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
      }

      // Finalize: trigger import
      const res = await axios.post('/foad/finalize-mbz-import', { upload_id: uploadId, filename: file.name }, { timeout: 180000 });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      setError(detail || (e.code === 'ECONNABORTED' ? 'Le fichier est trop volumineux ou le serveur met trop de temps. Reessayez.' : 'Erreur import: ' + (e.message || 'erreur reseau')));
    }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="moodle-import-modal">
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-orange-500 to-amber-500">
          <div className="flex items-center gap-3 text-white"><Download className="h-6 w-6" /><div><h2 className="text-lg font-bold">Importer depuis Moodle</h2><p className="text-sm text-white/80">Fichier .mbz (sauvegarde Moodle)</p></div></div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">
          {!result ? (
            <>
              {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">{error}</div>}
              {uploading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">Import en cours...</p>
                  <p className="text-slate-400 text-sm mt-1">Analyse du fichier, creation du cours et des questions...</p>
                </div>
              ) : (
                <div className="relative">
                  <input type="file" accept=".mbz" onChange={e => handleUpload(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" data-testid="mbz-file-input" />
                  <div className="border-2 border-dashed border-orange-300 rounded-xl p-10 text-center hover:border-orange-500 cursor-pointer bg-orange-50/50 transition-colors">
                    <Upload className="h-12 w-12 text-orange-400 mx-auto mb-3" />
                    <p className="text-slate-700 font-semibold text-lg">Cliquez ou glissez votre fichier .mbz</p>
                    <p className="text-slate-500 text-sm mt-2">Sauvegarde Moodle (.mbz) - Le cours, les chapitres, les quiz et toutes les questions seront importes automatiquement</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold mb-2"><CheckCircle className="h-5 w-5" /> Import reussi !</div>
                <p className="text-green-600 text-sm">{result.message}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-slate-800">{result.stats?.sections || 0}</p><p className="text-xs text-slate-500">Sections</p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-slate-800">{result.stats?.modules_created || 0}</p><p className="text-xs text-slate-500">Modules</p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-purple-600">{result.stats?.questions_created || 0}</p><p className="text-xs text-slate-500">Questions</p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-slate-800">{result.stats?.questions_total || 0}</p><p className="text-xs text-slate-500">Questions Moodle</p></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => { onImported(); onClose(); }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Voir le cours</Button>
                <Button variant="outline" onClick={onClose}>Fermer</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= MODULE TYPE CONFIG =============
const moduleTypeConfig = {
  chapitre: { icon: FolderOpen, label: 'Chapitre / Section', color: 'text-slate-700 bg-slate-100' },
  texte: { icon: FileText, label: 'Contenu texte', color: 'text-blue-600 bg-blue-50' },
  image: { icon: Image, label: 'Image', color: 'text-pink-600 bg-pink-50' },
  video: { icon: Youtube, label: 'Video YouTube', color: 'text-red-600 bg-red-50' },
  google_slides: { icon: Presentation, label: 'Google Slides', color: 'text-yellow-600 bg-yellow-50' },
  google_docs: { icon: FileText, label: 'Google Docs / Sheets', color: 'text-blue-600 bg-blue-50' },
  document: { icon: Upload, label: 'Document PDF/Word', color: 'text-amber-600 bg-amber-50' },
  quiz: { icon: HelpCircle, label: 'Quiz', color: 'text-purple-600 bg-purple-50' },
  devoir: { icon: PenLine, label: 'Devoir', color: 'text-orange-600 bg-orange-50' },
};

// ============= FULL SCREEN PREVIEW (STYLE APPRENANT) =============
const FullScreenPreview = ({ course, modules, onClose }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const m = modules[currentIdx];
  if (!m) return null;

  // Build chapter tree like apprenant view
  const chapterTree = [];
  let currentChapter = null;
  modules.forEach((mod, idx) => {
    if (mod.type === 'chapitre') {
      currentChapter = { ...mod, idx, children: [] };
      chapterTree.push(currentChapter);
    } else if (currentChapter) {
      currentChapter.children.push({ ...mod, idx });
    } else {
      chapterTree.push({ ...mod, idx, children: null });
    }
  });

  const renderContent = (mod) => {
    const cfg = moduleTypeConfig[mod.type] || moduleTypeConfig.texte;
    const ModIcon = cfg.icon;
    return (
      <>
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <span className={`p-2 rounded-lg flex-shrink-0 ${cfg.color}`}><ModIcon className="h-5 w-5 sm:h-6 sm:w-6" /></span>
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{mod.titre || cfg.label}</h1>
        </div>
        {mod.type === 'chapitre' && (<div>{mod.contenu && <img src={mod.contenu} alt="" className="w-full max-h-80 object-cover rounded-xl mb-6" />}{mod.description && <p className="text-base sm:text-lg text-slate-600 whitespace-pre-line">{mod.description}</p>}</div>)}
        {mod.type === 'texte' && <div className="prose prose-slate sm:prose-lg max-w-none break-words overflow-hidden [&_img]:max-w-full [&_img]:h-auto [&_iframe]:max-w-full [&_table]:max-w-full [&_pre]:overflow-x-auto" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(mod.contenu || '') }} />}
        {mod.type === 'image' && mod.contenu && (<div className="text-center"><img src={mod.contenu} alt={mod.titre} className="max-w-full max-h-[60vh] mx-auto rounded-xl shadow-lg" />{mod.description && <p className="text-slate-500 mt-3 italic">{mod.description}</p>}</div>)}
        {mod.type === 'video' && mod.video_url && (() => { const match = mod.video_url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?\s]+)/); return match ? <div className="aspect-video rounded-xl overflow-hidden shadow-lg"><iframe src={`https://www.youtube.com/embed/${match[1]}`} className="w-full h-full" allowFullScreen title={mod.titre} /></div> : null; })()}
        {mod.type === 'google_slides' && mod.embed_code && (<div className="rounded-xl overflow-hidden shadow-lg" style={{ position: 'relative', paddingBottom: '75%', height: 0 }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(mod.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"').replace(/<iframe/g, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"')) }} />)}
        {mod.type === 'google_docs' && mod.embed_code && (<div className="rounded-xl overflow-hidden shadow-lg border" style={{ height: '70vh' }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(mod.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"')) }} />)}
        {mod.type === 'google_docs' && !mod.embed_code && mod.contenu && (<div className="rounded-xl overflow-hidden shadow-lg border" style={{ height: '70vh' }}><iframe src={mod.contenu.includes('/pub') ? mod.contenu : mod.contenu.replace(/\/edit.*$/, '/pub?embedded=true')} className="w-full h-full border-0" title={mod.titre || 'Google Document'} /></div>)}
        {mod.type === 'document' && (
          <div>
            {mod.contenu && mod.contenu.startsWith('doc:') && (() => {
              const access = mod.document_access || 'consulter';
              const docUrl = `${process.env.REACT_APP_BACKEND_URL}/api/foad/documents/${mod.contenu.replace('doc:', '')}?token=${localStorage.getItem('token')}`;
              return (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <p className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><FileText className="h-5 w-5 text-amber-600" />{mod.description || 'Document'}</p>
                <p className="text-xs text-slate-500 mb-2">Acces: <span className="font-medium">{access === 'consulter' ? 'Consultation uniquement' : access === 'telecharger' ? 'Telechargement uniquement' : 'Consultation + Telechargement'}</span></p>
                {(access === 'consulter' || access === 'les_deux') && (
                  <iframe src={`${docUrl}#toolbar=0`} className="w-full rounded-lg border" style={{ height: '70vh' }} title={mod.description || 'Document'} />
                )}
                {(access === 'telecharger' || access === 'les_deux') && (
                  <a href={docUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium mt-3">
                    <Download className="h-4 w-4" /> Telecharger
                  </a>
                )}
              </div>
              );
            })()}
            {mod.embed_code && (<div className="rounded-xl overflow-hidden shadow-lg border" style={{ height: '70vh' }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(mod.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"')) }} />)}
          </div>
        )}
        {mod.type === 'devoir' && (<div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-6"><p className="font-semibold text-orange-800 mb-2">Devoir a rendre</p>{mod.description && <p className="text-sm text-orange-700">{mod.description}</p>}{mod.end_date && <p className="text-xs text-orange-500 mt-2">Date limite: {new Date(mod.end_date).toLocaleDateString('fr-FR')}</p>}</div>)}
        {(mod.type === 'quiz' || mod.type === 'question_intermediaire') && (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-purple-700">Quiz - {(mod.questions || []).length} question{(mod.questions || []).length > 1 ? 's' : ''} | Note de passage: {mod.note_passage || 5}/10</p>
            </div>
            {(mod.questions || []).map((q, qi) => (
              <div key={q.question_id} className="bg-slate-50 rounded-xl p-4 sm:p-6 border">
                <p className="font-semibold text-base sm:text-lg mb-3">Q{qi + 1}. {q.texte} <span className="text-purple-600 text-sm font-normal">({q.points} pts)</span></p>
                {q.type === 'qcm' && <div className="space-y-2 ml-2 sm:ml-4">{q.options.map((o, oi) => (<div key={oi} className="flex items-center gap-3 p-3 bg-white rounded-lg border"><div className="h-5 w-5 rounded-full border-2 border-slate-300 flex-shrink-0" /><span className="text-sm sm:text-base">{o.texte}</span></div>))}</div>}
                {q.type === 'qcm_multiple' && <div className="space-y-2 ml-2 sm:ml-4">{q.options.map((o, oi) => (<div key={oi} className="flex items-center gap-3 p-3 bg-white rounded-lg border"><div className="h-5 w-5 rounded border-2 border-slate-300 flex-shrink-0" /><span className="text-sm sm:text-base">{o.texte}</span></div>))}</div>}
                {q.type === 'vrai_faux' && <div className="space-y-2 ml-2 sm:ml-4">{(q.options || [{texte:'Vrai'},{texte:'Faux'}]).map((o, oi) => (<div key={oi} className="flex items-center gap-3 p-3 bg-white rounded-lg border"><div className="h-5 w-5 rounded-full border-2 border-slate-300 flex-shrink-0" /><span className="text-sm sm:text-base">{o.texte}</span></div>))}</div>}
                {q.type === 'texte_trou' && <div className="p-4 bg-white rounded-lg border"><p className="text-slate-600 italic text-sm">Texte a trou - {(q.blanks || []).length} champ(s) a remplir</p></div>}
                {q.type === 'appariement' && <div className="p-4 bg-white rounded-lg border"><p className="text-slate-600 italic text-sm">Appariement - {(q.pairs || []).length} paire(s) a relier</p></div>}
                {q.type === 'ouverte' && <textarea readOnly placeholder="Reponse du stagiaire..." className="w-full border rounded-lg p-3 bg-white h-24 text-sm" />}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="fixed inset-0 bg-white z-[100] flex flex-col" data-testid="fullscreen-preview">
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 bg-slate-900 text-white min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 overflow-hidden">
          <BookOpen className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <span className="font-semibold truncate text-sm sm:text-base">{course.titre}</span>
          <span className="text-slate-400 text-xs px-2 py-0.5 bg-slate-700 rounded-full hidden sm:inline">Apercu</span>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg flex-shrink-0"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="w-72 flex-shrink-0 bg-white border-r overflow-y-auto hidden lg:block">
          <div className="p-4 border-b bg-slate-50">
            <h3 className="font-bold text-sm text-slate-800 line-clamp-2">{course.titre}</h3>
            <p className="text-xs text-slate-400 mt-1">{modules.length} modules | {currentIdx + 1}/{modules.length}</p>
          </div>
          <div className="py-2">
            {chapterTree.map((item) => {
              if (item.children) {
                return (
                  <div key={item.module_id || item.idx}>
                    <div className="px-4 py-2.5 flex items-center gap-2 text-slate-700">
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold bg-slate-100 text-slate-500"><BookOpen className="h-3 w-3" /></div>
                      <span className="text-xs font-bold uppercase tracking-wide">{item.titre}</span>
                    </div>
                    {item.contenu && <div className="mx-4 mb-2 rounded overflow-hidden h-10 w-16 flex-shrink-0"><img src={item.contenu} alt="" className="w-full h-full object-cover rounded" /></div>}
                    {item.children.map(child => {
                      const isActive = currentIdx === child.idx;
                      const ChildIcon = (moduleTypeConfig[child.type] || moduleTypeConfig.texte).icon;
                      return (
                        <button key={child.module_id} onClick={() => setCurrentIdx(child.idx)}
                          className={`w-full text-left pl-10 pr-4 py-2 flex items-center gap-2 text-xs transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600' : 'hover:bg-slate-50 text-slate-600 cursor-pointer'}`}>
                          <ChildIcon className="h-3 w-3 flex-shrink-0" /><span className="truncate">{child.titre}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              }
              const isActive = currentIdx === item.idx;
              const ItemIcon = (moduleTypeConfig[item.type] || moduleTypeConfig.texte).icon;
              return (
                <button key={item.module_id} onClick={() => setCurrentIdx(item.idx)}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-2 text-xs transition-colors ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600' : 'hover:bg-slate-50 text-slate-600 cursor-pointer'}`}>
                  <ItemIcon className="h-3 w-3 flex-shrink-0" /><span className="truncate">{item.titre}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-3 sm:p-8 max-w-4xl mx-auto w-full">{renderContent(m)}</div>
        </div>
      </div>
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 bg-slate-100 border-t">
        <Button variant="outline" onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0}><ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" /> Precedent</Button>
        <span className="text-xs text-slate-400 lg:hidden">{currentIdx + 1} / {modules.length}</span>
        <Button onClick={() => setCurrentIdx(Math.min(modules.length - 1, currentIdx + 1))} disabled={currentIdx === modules.length - 1} className="bg-blue-600 hover:bg-blue-700 text-white">Suivant <ChevronDown className="h-4 w-4 ml-1 sm:ml-2 rotate-[-90deg]" /></Button>
      </div>
    </div>
  );
};

// ============= QUESTION EDITOR (ENHANCED) =============
const QUESTION_TYPES = [
  { value: 'qcm', label: 'QCM (1 reponse)', icon: HelpCircle },
  { value: 'qcm_multiple', label: 'QCM Multiple', icon: CheckCircle },
  { value: 'vrai_faux', label: 'Vrai / Faux', icon: ToggleLeft },
  { value: 'texte_trou', label: 'Texte a trou', icon: FileQuestion },
  { value: 'appariement', label: 'Appariement', icon: Shuffle },
  { value: 'ouverte', label: 'Question ouverte', icon: PenLine },
];

// ============= DEVOIR SUBMISSIONS PANEL (FORMATEUR) =============
const DevoirSubmissionsPanel = ({ courseId, moduleId, noteMax }) => {
  const [submissions, setSubmissions] = useState([]);
  const [show, setShow] = useState(false);
  const [gradingId, setGradingId] = useState(null);
  const [gradeNote, setGradeNote] = useState('');
  const [gradeComment, setGradeComment] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSubmissions = async () => {
    try {
      const r = await axios.get(`/foad/courses/${courseId}/modules/${moduleId}/submissions`);
      setSubmissions(r.data || []);
    } catch {}
  };

  const toggleShow = () => {
    if (!show) loadSubmissions();
    setShow(!show);
  };

  const downloadFile = async (submissionId, filename) => {
    try {
      const response = await axios.get(`/foad/submissions/${submissionId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch { toast.error('Erreur telechargement'); }
  };

  const startGrading = (sub) => {
    setGradingId(sub.submission_id);
    setGradeNote(sub.note !== null ? String(sub.note) : '');
    setGradeComment(sub.commentaire || '');
  };

  const saveGrade = async () => {
    if (!gradeNote) { toast.error('Note requise'); return; }
    setSaving(true);
    try {
      await axios.put(`/foad/submissions/${gradingId}/grade`, { note: parseFloat(gradeNote), commentaire: gradeComment });
      toast.success('Note enregistree');
      setGradingId(null);
      loadSubmissions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t pt-4 mt-4">
      <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={toggleShow} data-testid="toggle-submissions">
        <Users className="h-3.5 w-3.5 mr-1.5" /> {show ? 'Masquer' : 'Voir'} les soumissions
      </Button>

      {show && (
        <div className="mt-3 space-y-2" data-testid="submissions-list">
          {submissions.length === 0 ? (
            <p className="text-sm text-slate-400 py-3 text-center">Aucun devoir soumis pour le moment.</p>
          ) : (
            submissions.map(sub => (
              <div key={sub.submission_id} className={`border rounded-lg p-3 ${sub.status === 'graded' ? 'bg-green-50 border-green-200' : 'bg-white'}`} data-testid={`submission-${sub.submission_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{sub.user_name || 'Stagiaire'}</p>
                    <p className="text-xs text-slate-500">
                      {sub.filename} ({(sub.file_size / 1024).toFixed(0)} Ko) - {new Date(sub.submitted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {sub.status === 'graded' && (
                      <p className="text-xs font-bold mt-1 text-green-700">Note: {sub.note}/{sub.note_max || noteMax}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="h-7 text-blue-600" onClick={() => downloadFile(sub.submission_id, sub.filename)} data-testid={`download-${sub.submission_id}`}>
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className={`h-7 ${sub.status === 'graded' ? 'text-green-600' : 'text-orange-600'}`} onClick={() => startGrading(sub)} data-testid={`grade-${sub.submission_id}`}>
                      <Award className="h-3.5 w-3.5 mr-1" /> {sub.status === 'graded' ? 'Modifier' : 'Noter'}
                    </Button>
                  </div>
                </div>

                {/* Grading form */}
                {gradingId === sub.submission_id && (
                  <div className="mt-3 pt-3 border-t space-y-2" data-testid="grading-form">
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Note (/{noteMax})</label>
                        <input type="number" min={0} max={noteMax} step={0.5} value={gradeNote} onChange={e => setGradeNote(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="grade-note-input" />
                      </div>
                      <div className="col-span-3">
                        <label className="text-xs font-medium text-slate-600 block mb-1">Commentaire</label>
                        <input value={gradeComment} onChange={e => setGradeComment(e.target.value)} placeholder="Commentaire..." className="w-full border rounded px-2 py-1.5 text-sm" data-testid="grade-comment-input" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs" onClick={saveGrade} disabled={saving} data-testid="save-grade-btn">
                        <Save className="h-3 w-3 mr-1" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setGradingId(null)}>Annuler</Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};



const QuestionEditor = ({ questions, onChange, moduleId }) => {
  const [showBankPicker, setShowBankPicker] = useState(false);
  const [bankThemes, setBankThemes] = useState([]);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [selectedBankTheme, setSelectedBankTheme] = useState('');
  const [exportThemes, setExportThemes] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportTheme, setSelectedExportTheme] = useState('');

  const loadBankThemes = async () => {
    try { const r = await axios.get('/foad/question-bank/themes'); setBankThemes(r.data); setExportThemes(r.data); } catch {}
  };
  const loadBankQuestions = async (themeId) => {
    try { const r = await axios.get(`/foad/question-bank/questions?theme_id=${themeId}`); setBankQuestions(r.data); } catch {}
  };

  const add = (type) => {
    const base = { question_id: `new_${Date.now()}`, texte: '', type, options: [], points: 1, bareme_detail: '', explication: '', obligatoire: true, pairs: [], blanks: [] };
    if (type === 'qcm' || type === 'qcm_multiple') base.options = [{ texte: '', correct: false, points: 0 }, { texte: '', correct: false, points: 0 }];
    if (type === 'vrai_faux') base.options = [{ texte: 'Vrai', correct: true, points: 0 }, { texte: 'Faux', correct: false, points: 0 }];
    if (type === 'appariement') base.pairs = [{ left: '', right: '' }, { left: '', right: '' }];
    if (type === 'texte_trou') { base.texte = 'Le ___ est ___'; base.blanks = ['', '']; }
    onChange([...questions, base]);
  };
  const upd = (i, f, v) => { const u = [...questions]; u[i] = { ...u[i], [f]: v }; onChange(u); };
  const rm = (i) => onChange(questions.filter((_, j) => j !== i));
  const addOpt = (qi) => { const u = [...questions]; u[qi].options = [...u[qi].options, { texte: '', correct: false, points: 0 }]; onChange(u); };
  const updOpt = (qi, oi, f, v) => { const u = [...questions]; u[qi].options = [...u[qi].options]; u[qi].options[oi] = { ...u[qi].options[oi], [f]: v }; onChange(u); };
  const rmOpt = (qi, oi) => { const u = [...questions]; u[qi].options = u[qi].options.filter((_, j) => j !== oi); onChange(u); };
  const addPair = (qi) => { const u = [...questions]; u[qi].pairs = [...(u[qi].pairs || []), { left: '', right: '' }]; onChange(u); };
  const updPair = (qi, pi, side, v) => { const u = [...questions]; u[qi].pairs = [...u[qi].pairs]; u[qi].pairs[pi] = { ...u[qi].pairs[pi], [side]: v }; onChange(u); };
  const rmPair = (qi, pi) => { const u = [...questions]; u[qi].pairs = u[qi].pairs.filter((_, j) => j !== pi); onChange(u); };
  const updBlank = (qi, bi, v) => { const u = [...questions]; u[qi].blanks = [...(u[qi].blanks || [])]; u[qi].blanks[bi] = v; onChange(u); };
  const total = questions.reduce((s, q) => s + (q.points || 1), 0);

  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center justify-between">
        <span className="text-sm font-medium text-purple-700">Bareme total : {total} point{total > 1 ? 's' : ''} (note /10 = proportionnel)</span>
        <span className="text-xs text-purple-500">{questions.length} question{questions.length > 1 ? 's' : ''}</span>
      </div>

      {questions.map((q, qi) => {
        const qtype = QUESTION_TYPES.find(t => t.value === q.type) || QUESTION_TYPES[0];
        const QIcon = qtype.icon;
        return (
          <div key={q.question_id} className="border rounded-lg p-4 bg-white" data-testid={`question-block-${qi}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <QIcon className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium text-slate-500">Q{qi + 1} - {qtype.label}</span>
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">{q.points || 1} pt{(q.points || 1) > 1 ? 's' : ''}</span>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => rm(qi)}><X className="h-3.5 w-3.5" /></Button>
            </div>

            {/* Intitule */}
            <input value={q.texte} onChange={e => upd(qi, 'texte', e.target.value)} placeholder={q.type === 'texte_trou' ? 'Le ___ est ___ (utilisez ___ pour les trous)' : 'Intitule de la question...'} className="w-full border rounded-lg px-3 py-2 text-sm mb-3" data-testid={`question-text-${qi}`} />

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><label className="text-xs text-slate-500 mb-1 block">Points</label><input type="number" min={0} max={100} step={0.5} value={q.points || 1} onChange={e => upd(qi, 'points', parseFloat(e.target.value) || 1)} className="w-full border rounded-lg px-3 py-1.5 text-sm" data-testid={`question-points-${qi}`} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-500 mb-1 block">Detail du bareme</label><input value={q.bareme_detail || ''} onChange={e => upd(qi, 'bareme_detail', e.target.value)} placeholder="Ex: -0.5 par erreur..." className="w-full border rounded-lg px-3 py-1.5 text-sm" /></div>
            </div>

            {/* QCM / QCM MULTIPLE */}
            {(q.type === 'qcm' || q.type === 'qcm_multiple') && (
              <div className="space-y-2 ml-4">
                {q.type === 'qcm_multiple' && <p className="text-xs text-blue-600 mb-1">Plusieurs reponses correctes possibles</p>}
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button type="button" onClick={() => {
                      if (q.type === 'qcm') {
                        // Single: uncheck all others
                        q.options.forEach((_, ooi) => updOpt(qi, ooi, 'correct', ooi === oi ? !o.correct : false));
                      } else {
                        updOpt(qi, oi, 'correct', !o.correct);
                      }
                    }} className={`h-5 w-5 ${q.type === 'qcm' ? 'rounded-full' : 'rounded'} border-2 flex items-center justify-center flex-shrink-0 ${o.correct ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300'}`} data-testid={`option-correct-${qi}-${oi}`}>
                      {o.correct && <CheckCircle className="h-3 w-3" />}
                    </button>
                    <input value={o.texte} onChange={e => updOpt(qi, oi, 'texte', e.target.value)} placeholder={`Reponse ${oi + 1}...`} className="flex-1 border rounded px-2 py-1.5 text-sm" data-testid={`option-text-${qi}-${oi}`} />
                    {q.options.length > 2 && <Button variant="ghost" size="sm" className="h-7 text-slate-400" onClick={() => rmOpt(qi, oi)}><X className="h-3 w-3" /></Button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-blue-600 text-xs" onClick={() => addOpt(qi)}>+ Ajouter reponse</Button>
              </div>
            )}

            {/* VRAI / FAUX */}
            {q.type === 'vrai_faux' && (
              <div className="space-y-2 ml-4">
                <p className="text-xs text-slate-500 mb-1">Selectionnez la bonne reponse :</p>
                {(q.options || []).map((o, oi) => (
                  <div key={oi} className="flex items-center gap-3">
                    <button type="button" onClick={() => {
                      q.options.forEach((_, ooi) => updOpt(qi, ooi, 'correct', ooi === oi));
                    }} className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${o.correct ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300'}`} data-testid={`vf-correct-${qi}-${oi}`}>
                      {o.correct && <CheckCircle className="h-3 w-3" />}
                    </button>
                    <span className="text-sm font-medium">{o.texte}</span>
                  </div>
                ))}
              </div>
            )}

            {/* TEXTE A TROU */}
            {q.type === 'texte_trou' && (
              <div className="ml-4 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 mb-1">Utilisez <code className="bg-amber-100 px-1 rounded">___</code> dans l'intitule pour marquer les trous.</p>
                  <p className="text-xs text-amber-600">Exemple: "Le <code className="bg-amber-100 px-1 rounded">___</code> est la capitale de la France"</p>
                </div>
                <p className="text-xs font-medium text-slate-600">Reponses correctes pour chaque trou :</p>
                {(q.blanks || []).map((b, bi) => (
                  <div key={bi} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 w-16">Trou {bi + 1} :</span>
                    <input value={b} onChange={e => updBlank(qi, bi, e.target.value)} placeholder="Reponse correcte..." className="flex-1 border rounded px-2 py-1.5 text-sm" data-testid={`blank-answer-${qi}-${bi}`} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-blue-600 text-xs" onClick={() => { const u = [...questions]; u[qi].blanks = [...(u[qi].blanks || []), '']; onChange(u); }}>+ Ajouter trou</Button>
                  {(q.blanks || []).length > 1 && <Button variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => { const u = [...questions]; u[qi].blanks = u[qi].blanks.slice(0, -1); onChange(u); }}>- Retirer dernier</Button>}
                </div>
              </div>
            )}

            {/* APPARIEMENT */}
            {q.type === 'appariement' && (
              <div className="ml-4 space-y-3">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-xs text-indigo-700">Le stagiaire devra relier les elements de la colonne gauche avec ceux de la colonne droite.</p>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                  <span className="text-xs font-bold text-slate-500 text-center">Colonne A</span>
                  <span />
                  <span className="text-xs font-bold text-slate-500 text-center">Colonne B</span>
                  <span />
                  {(q.pairs || []).map((p, pi) => (
                    <React.Fragment key={pi}>
                      <input value={p.left} onChange={e => updPair(qi, pi, 'left', e.target.value)} placeholder={`Element ${pi + 1}...`} className="border rounded px-2 py-1.5 text-sm" data-testid={`pair-left-${qi}-${pi}`} />
                      <Shuffle className="h-4 w-4 text-indigo-400 mx-1" />
                      <input value={p.right} onChange={e => updPair(qi, pi, 'right', e.target.value)} placeholder={`Correspondance ${pi + 1}...`} className="border rounded px-2 py-1.5 text-sm" data-testid={`pair-right-${qi}-${pi}`} />
                      {(q.pairs || []).length > 2 && <Button variant="ghost" size="sm" className="h-7 text-slate-400" onClick={() => rmPair(qi, pi)}><X className="h-3 w-3" /></Button>}
                      {(q.pairs || []).length <= 2 && <span />}
                    </React.Fragment>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="text-blue-600 text-xs" onClick={() => addPair(qi)}>+ Ajouter paire</Button>
              </div>
            )}

            {/* OUVERTE */}
            {q.type === 'ouverte' && <div className="ml-4 p-3 bg-slate-50 rounded text-xs text-slate-500 italic">Reponse libre du stagiaire (corrigee par le formateur).</div>}

            {/* Explication */}
            <div className="mt-3"><label className="text-xs text-slate-500 mb-1 block">Explication (visible apres reponse)</label><textarea value={q.explication || ''} onChange={e => upd(qi, 'explication', e.target.value)} placeholder="Explication..." rows={2} className="w-full border rounded-lg px-3 py-1.5 text-sm" /></div>
            <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer text-slate-600"><input type="checkbox" checked={q.obligatoire !== false} onChange={e => upd(qi, 'obligatoire', e.target.checked)} className="rounded" /> Obligatoire</label>
          </div>
        );
      })}

      {/* Add question - direct buttons */}
      <div>
        <p className="text-xs font-semibold text-purple-500 uppercase mb-2">Ajouter une question</p>
        <div className="flex flex-wrap gap-2">
          {QUESTION_TYPES.map(qt => {
            const QI = qt.icon;
            return (
              <Button key={qt.value} variant="outline" size="sm" onClick={() => add(qt.value)}
                className="text-purple-700 border-purple-200 hover:bg-purple-50" data-testid={`add-q-${qt.value}`}>
                <QI className="h-3.5 w-3.5 mr-1.5" /> {qt.label}
              </Button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-dashed">
          <Button variant="outline" size="sm" className="text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => { loadBankThemes(); setShowBankPicker(true); }} data-testid="pick-from-bank">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Piocher dans la banque
          </Button>
          {moduleId && !moduleId.startsWith('new_') && (
            <Button variant="outline" size="sm" className="text-green-700 border-green-200 hover:bg-green-50" onClick={() => { loadBankThemes(); setShowExportModal(true); }} data-testid="export-to-bank">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Exporter vers la banque
            </Button>
          )}
        </div>
      </div>

      {/* Bank Picker Modal */}
      {showBankPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBankPicker(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="bank-picker-modal">
            <div className="p-4 border-b bg-indigo-50 flex items-center justify-between">
              <h3 className="font-bold text-indigo-800 flex items-center gap-2"><Database className="h-5 w-5" /> Piocher dans la banque</h3>
              <button onClick={() => setShowBankPicker(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-4">
              <select value={selectedBankTheme} onChange={e => { setSelectedBankTheme(e.target.value); if (e.target.value) loadBankQuestions(e.target.value); }} className="w-full border rounded-lg px-3 py-2 text-sm mb-3" data-testid="bank-theme-select">
                <option value="">-- Choisir un theme --</option>
                {bankThemes.map(t => <option key={t.theme_id} value={t.theme_id}>{t.name} ({t.question_count})</option>)}
              </select>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {bankQuestions.filter(q => q.type === 'qcm' || q.type === 'vrai_faux' || q.type === 'qcm_multiple').map(bq => (
                  <div key={bq.bank_question_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border hover:bg-indigo-50 cursor-pointer" onClick={() => {
                    const newQ = { question_id: `new_${Date.now()}_${Math.random().toString(36).substr(2,5)}`, texte: bq.texte, type: bq.type, options: bq.options, points: bq.points || 1, bareme_detail: '', explication: bq.explication || '', obligatoire: true, pairs: bq.pairs || [], blanks: bq.blanks || [] };
                    onChange([...questions, newQ]);
                    toast.success('Question ajoutee depuis la banque');
                  }}>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{bq.texte}</p>
                      <span className="text-xs text-indigo-500">{bq.type}</span>
                    </div>
                    <Plus className="h-4 w-4 text-indigo-500" />
                  </div>
                ))}
                {bankQuestions.length === 0 && selectedBankTheme && <p className="text-sm text-slate-400 text-center py-4">Aucune question dans ce theme</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export to Bank Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowExportModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="export-bank-modal">
            <div className="p-4 border-b bg-green-50">
              <h3 className="font-bold text-green-800 flex items-center gap-2"><Upload className="h-5 w-5" /> Exporter vers la banque</h3>
              <p className="text-xs text-green-600 mt-1">Les {questions.length} question(s) de ce quiz seront ajoutees a la banque</p>
            </div>
            <div className="p-4">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Theme de destination</label>
              <select value={selectedExportTheme} onChange={e => setSelectedExportTheme(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mb-3" data-testid="export-theme-select">
                <option value="">-- Choisir un theme --</option>
                {exportThemes.map(t => <option key={t.theme_id} value={t.theme_id}>{t.name}</option>)}
              </select>
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={!selectedExportTheme} onClick={async () => {
                try {
                  const r = await axios.post('/foad/question-bank/import-from-quiz', { module_id: moduleId, theme_id: selectedExportTheme });
                  toast.success(r.data.message);
                  setShowExportModal(false);
                } catch (e) { toast.error(e.response?.data?.detail || 'Erreur export'); }
              }} data-testid="confirm-export-btn">
                <Upload className="h-4 w-4 mr-2" /> Exporter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= STAGIAIRES PANEL =============
const StagiairesPanel = ({ courseId, inscrits, onUpdate }) => {
  const [all, setAll] = useState([]);
  const [centres, setCentres] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollingCentre, setEnrollingCentre] = useState(null);
  useEffect(() => {
    axios.get('/foad/stagiaires-disponibles').then(r => setAll(r.data || [])).catch(() => {});
    axios.get('/foad/centres-disponibles').then(r => setCentres(r.data || [])).catch(() => {});
  }, []);
  const available = all.filter(s => !(inscrits || []).includes(s.user_id) && (`${s.prenom} ${s.nom} ${s.email}`.toLowerCase().includes(search.toLowerCase())));
  const inscritDetails = all.filter(s => (inscrits || []).includes(s.user_id));
  const inscrire = async (uid) => { setLoading(true); try { await axios.post(`/foad/courses/${courseId}/inscrire`, { user_ids: [uid] }); toast.success('Inscrit'); onUpdate(); } catch { toast.error('Erreur'); } finally { setLoading(false); } };
  const desinscrire = async (uid) => { try { await axios.post(`/foad/courses/${courseId}/desinscrire`, { user_id: uid }); toast.success('Desinscrit'); onUpdate(); } catch { toast.error('Erreur'); } };
  const inscrireCentre = async (centreNom) => {
    setEnrollingCentre(centreNom);
    try {
      const r = await axios.post(`/foad/courses/${courseId}/inscrire-centre`, { centre_nom: centreNom });
      toast.success(r.data.message);
      onUpdate();
      // Refresh available list
      const res = await axios.get('/foad/stagiaires-disponibles');
      setAll(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur inscription centre'); }
    finally { setEnrollingCentre(null); }
  };

  return (
    <div className="bg-white rounded-xl border p-6 mb-6" data-testid="stagiaires-panel">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" /> Stagiaires inscrits ({(inscrits || []).length})</h3>
      {inscritDetails.length === 0 ? (
        <p className="text-sm text-slate-400 mb-4">Aucun stagiaire inscrit a ce cours.</p>
      ) : (
        <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
          {inscritDetails.map(s => (
            <div key={s.user_id} className="flex items-center justify-between p-2.5 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-700">{(s.prenom?.[0] || '')}{(s.nom?.[0] || '')}</div>
                <div><p className="text-sm font-medium">{s.prenom} {s.nom}</p><p className="text-xs text-slate-500">{s.email}</p></div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => desinscrire(s.user_id)} data-testid={`desinscrire-${s.user_id}`}><UserMinus className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Inscription par centre */}
      <div className="border-t pt-4 mb-4">
        <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-green-600" /> Inscrire par centre
        </p>
        {centres.filter(c => c.nb_stagiaires > 0).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {centres.filter(c => c.nb_stagiaires > 0).map(c => (
              <Button key={c.nom} variant="outline" size="sm"
                className="text-green-700 border-green-200 hover:bg-green-50"
                onClick={() => inscrireCentre(c.nom)}
                disabled={enrollingCentre === c.nom}
                data-testid={`inscrire-centre-${c.nom}`}>
                {enrollingCentre === c.nom ? (
                  <span className="animate-spin mr-1.5 h-3 w-3 border-2 border-green-500 border-t-transparent rounded-full inline-block" />
                ) : (
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                )}
                {c.nom} ({c.nb_stagiaires})
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 bg-slate-50 rounded-lg p-3">
            Aucun centre avec des stagiaires. Creez des centres et rattachez-y des stagiaires pour pouvoir les inscrire en masse.
          </p>
        )}
      </div>

      <div className="border-t pt-4">
        <p className="text-sm font-medium text-slate-600 mb-2">Ajouter individuellement</p>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou email..." className="w-full border rounded-lg px-3 py-2 text-sm mb-2" data-testid="search-stagiaire" />
        <div className="max-h-48 overflow-y-auto space-y-1">
          {available.slice(0, 20).map(s => (
            <div key={s.user_id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{(s.prenom?.[0] || '')}{(s.nom?.[0] || '')}</div>
                <div><p className="text-sm">{s.prenom} {s.nom}</p><p className="text-xs text-slate-400">{s.email}</p></div>
              </div>
              <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => inscrire(s.user_id)} disabled={loading} data-testid={`inscrire-${s.user_id}`}><UserPlus className="h-3 w-3 mr-1" /> Inscrire</Button>
            </div>
          ))}
          {available.length === 0 && <p className="text-xs text-slate-400 text-center py-3">{search ? 'Aucun resultat' : 'Tous les stagiaires sont inscrits'}</p>}
        </div>
      </div>
    </div>
  );
};

// ============= QUIZ DETAIL MODAL =============
const QuizDetailModal = ({ courseId, moduleId, userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`/foad/courses/${courseId}/quiz-detail/${moduleId}/${userId}`)
      .then(r => setData(r.data)).catch(() => toast.error('Erreur chargement détail'))
      .finally(() => setLoading(false));
  }, [courseId, moduleId, userId]);

  if (loading) return <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-3 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-3" onClick={onClose} data-testid="quiz-detail-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 sm:p-5 border-b bg-slate-50 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="font-bold text-base sm:text-lg text-slate-800 truncate">{data.stagiaire_nom}</h3>
            <p className="text-xs sm:text-sm text-slate-500">{data.module_titre} — {data.note_sur_10}/10 ({data.nb_tentatives} tentative{data.nb_tentatives > 1 ? 's' : ''})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg flex-shrink-0"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {data.detail.map((q, i) => (
            <div key={q.question_id} className={`rounded-xl border-2 p-3 sm:p-4 ${q.correct === true ? 'border-green-200 bg-green-50/50' : q.correct === false ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid={`quiz-detail-q-${i}`}>
              <div className="flex items-start gap-2 mb-2">
                <span className="flex-shrink-0 mt-0.5">
                  {q.correct === true ? <CheckCircle className="h-5 w-5 text-green-600" /> : q.correct === false ? <XCircle className="h-5 w-5 text-red-500" /> : <HelpCircle className="h-5 w-5 text-amber-500" />}
                </span>
                <p className="font-semibold text-sm sm:text-base text-slate-800">Q{i + 1}. {q.texte} <span className="text-xs font-normal text-slate-400">({q.points} pt{q.points > 1 ? 's' : ''})</span></p>
              </div>

              {/* QCM / QCM Multiple / Vrai-Faux */}
              {q.options && (
                <div className="ml-7 space-y-1.5">
                  {q.options.map((opt, oi) => {
                    const wasSelected = (q.selected || []).includes(oi);
                    const isCorrectOpt = opt.correct;
                    let cls = 'bg-white border-slate-200';
                    if (isCorrectOpt && wasSelected) cls = 'bg-green-100 border-green-400';
                    else if (isCorrectOpt && !wasSelected) cls = 'bg-green-50 border-green-300 border-dashed';
                    else if (!isCorrectOpt && wasSelected) cls = 'bg-red-100 border-red-400';
                    return (
                      <div key={oi} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${cls}`}>
                        {wasSelected ? (isCorrectOpt ? <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />) : (isCorrectOpt ? <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" /> : <div className="h-4 w-4 rounded-full border-2 border-slate-300 flex-shrink-0" />)}
                        <span className={wasSelected ? 'font-medium' : ''}>{opt.texte}</span>
                        {isCorrectOpt && !wasSelected && <span className="text-xs text-green-600 ml-auto">(bonne reponse)</span>}
                        {!isCorrectOpt && wasSelected && <span className="text-xs text-red-500 ml-auto">(choisi)</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Texte a trous */}
              {q.type === 'texte_trou' && (
                <div className="ml-7 space-y-1.5">
                  {(q.correct_blanks || []).map((cb, bi) => {
                    const ub = (q.user_blanks || [])[bi] || '';
                    const ok = ub.trim().toLowerCase() === cb.trim().toLowerCase();
                    return (
                      <div key={bi} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${ok ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'}`}>
                        {ok ? <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                        <span>Trou {bi + 1}: <strong>{ub || '(vide)'}</strong></span>
                        {!ok && <span className="text-xs text-green-700 ml-auto">Attendu: <strong>{cb}</strong></span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Appariement */}
              {q.type === 'appariement' && (
                <div className="ml-7 space-y-1.5">
                  {(q.correct_pairs || []).map((cp, pi) => {
                    const up = (q.user_pairs || [])[pi];
                    const ok = up && up.left === cp.left && up.right === cp.right;
                    return (
                      <div key={pi} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${ok ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'}`}>
                        {ok ? <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                        <span>{cp.left} → {up ? up.right : '?'}</span>
                        {!ok && <span className="text-xs text-green-700 ml-auto">Attendu: {cp.right}</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Question ouverte */}
              {q.type === 'ouverte' && (
                <div className="ml-7 p-3 rounded-lg border bg-amber-50 border-amber-200 text-sm">
                  <p className="text-xs text-amber-600 mb-1 font-medium">Reponse du stagiaire (a evaluer) :</p>
                  <p className="text-slate-700">{q.user_text || '(pas de reponse)'}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============= QUIZ SCORES DASHBOARD =============
const QuizScoresDashboard = ({ courseId, courseData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certData, setCertData] = useState(null);
  const [detailView, setDetailView] = useState(null); // { moduleId, userId }
  useEffect(() => { axios.get(`/foad/courses/${courseId}/quiz-scores`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [courseId]);

  const showCert = async (row) => {
    const bestNotes = row.quizzes.filter(q => q.note_sur_10 !== null).map(q => q.note_sur_10);
    const avg = bestNotes.length > 0 ? (bestNotes.reduce((a, b) => a + b, 0) / bestNotes.length).toFixed(1) : 0;
    let tamponImg = '', gerantNom = '';
    try {
      const stampRes = await axios.get('/foad/centre-stamp');
      tamponImg = stampRes.data.tampon_img || '';
      gerantNom = stampRes.data.gerant_nom || '';
    } catch {}
    const duree = courseData?.duree_estimee || '';
    setCertData({
      prenom: row.prenom, nom: row.nom,
      courseTitre: data.course_titre || courseData?.titre || '',
      courseDescription: courseData?.description || '',
      objectifs: courseData?.objectifs || '',
      dureeEstimee: duree ? (String(duree).match(/heure|h|min/i) ? duree : duree + ' heures') : '',
      noteSur10: avg, nbModules: courseData?.nb_modules || 0,
      dateStr: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      refNumber: `${courseId.substring(0, 8).toUpperCase()}`,
      tamponImg, gerantNom
    });
  };

  if (loading) return <div className="flex justify-center p-6"><div className="animate-spin h-6 w-6 border-3 border-purple-500 border-t-transparent rounded-full" /></div>;
  if (!data || !data.quiz_modules?.length) return <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500 text-sm">Aucun quiz dans ce cours ou aucun stagiaire inscrit.</div>;

  return (
    <div className="bg-white rounded-xl border p-6 mb-6" data-testid="quiz-scores-dashboard">
      {certData && <CertificateFOAD data={certData} onClose={() => setCertData(null)} />}
      {detailView && <QuizDetailModal courseId={courseId} moduleId={detailView.moduleId} userId={detailView.userId} onClose={() => setDetailView(null)} />}
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-purple-600" /> Tableau de notes</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left p-3 font-semibold text-slate-700">Stagiaire</th>
              {data.quiz_modules.map(qm => (
                <th key={qm.module_id} className="text-center p-3 font-semibold text-slate-700">{qm.titre}</th>
              ))}
              <th className="text-center p-3 font-semibold text-slate-700">Certificat</th>
            </tr>
          </thead>
          <tbody>
            {data.scores.map(row => {
              const allPassed = row.quizzes.length > 0 && row.quizzes.every(qz => qz.reussi);
              return (
                <tr key={row.user_id} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-medium">{row.prenom} {row.nom}</td>
                  {row.quizzes.map(qz => (
                    <td key={qz.module_id} className="text-center p-3">
                      {qz.note_sur_10 !== null ? (
                        <div>
                          <button
                            onClick={() => setDetailView({ moduleId: qz.module_id, userId: row.user_id })}
                            className="hover:underline cursor-pointer"
                            title="Voir le detail des reponses"
                            data-testid={`quiz-detail-btn-${qz.module_id}-${row.user_id}`}
                          >
                            <span className={`font-bold ${qz.reussi ? 'text-green-600' : 'text-red-500'}`}>{qz.note_sur_10}/10</span>
                          </button>
                          <span className="text-xs text-slate-400 block">{qz.nb_tentatives} essai{qz.nb_tentatives > 1 ? 's' : ''}</span>
                          <button
                            onClick={() => setDetailView({ moduleId: qz.module_id, userId: row.user_id })}
                            className="text-xs text-blue-500 hover:text-blue-700 hover:underline mt-0.5 inline-flex items-center gap-0.5"
                            data-testid={`quiz-see-detail-${qz.module_id}-${row.user_id}`}
                          >
                            <Eye className="h-3 w-3" /> Detail
                          </button>
                        </div>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                  ))}
                  <td className="text-center p-3">
                    {allPassed ? (
                      <button onClick={() => showCert(row)} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold hover:bg-green-200 transition-colors cursor-pointer" data-testid={`view-cert-${row.user_id}`}>
                        <Trophy className="h-3 w-3" /> Voir certificat
                      </button>
                    ) : <span className="text-slate-400 text-xs">-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============= COURSE LIST =============
const CourseList = ({ onSelect, onNew, user }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMoodleImport, setShowMoodleImport] = useState(false);
  const [certListData, setCertListData] = useState(null);
  const [certPreviewData, setCertPreviewData] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(async () => { try { const r = await axios.get('/foad/courses'); setCourses(r.data); } catch {} finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id, t) => {
    if (!window.confirm(`ATTENTION : Cette action est irreversible.\n\nToutes les donnees du cours "${t}" (modules, quiz, soumissions) seront definitivement supprimees.\n\nConfirmez-vous la suppression ?`)) return;
    try { await axios.delete(`/foad/courses/${id}`); toast.success('Cours supprime'); load(); } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="foad-title">Formations FOAD</h1><p className="text-slate-500 text-sm mt-1">{courses.length} cours</p></div>
        {(user.role === 'centre' || user.role === 'formateur') && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowMoodleImport(true)} variant="outline" size="sm" className="border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="foad-import-moodle"><Download className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Moodle</span></Button>
            <Button onClick={onNew} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="foad-new-course"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Nouveau</span></Button>
          </div>
        )}
      </div>

      {/* Quick access cards - visible for formateur, centre, responsable */}
      {(user.role === 'centre' || user.role === 'formateur' || user.role === 'responsable') && (
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="foad-quick-access">
          <button onClick={() => navigate('/question-bank')} className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white text-left hover:shadow-lg hover:shadow-indigo-200 transition-all group" data-testid="quick-question-bank">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Banque de Questions</h3>
                <p className="text-xs text-indigo-200">Organiser, creer et piocher</p>
              </div>
            </div>
          </button>
          <button onClick={() => navigate('/foad-kahoot')} className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 text-white text-left hover:shadow-lg hover:shadow-violet-200 transition-all group" data-testid="quick-kahoot">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Gamepad2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Kahoot Live</h3>
                <p className="text-xs text-violet-200">Quiz interactif en temps reel</p>
              </div>
            </div>
          </button>
        </div>
      )}

      {courses.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500 text-lg">Aucun cours</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map(c => {
            const nbInscrits = (c.stagiaires_inscrits || []).length;
            const progression = c.avg_progression || 0;
            return (
              <div key={c.course_id} className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all cursor-pointer group" data-testid={`course-card-${c.course_id}`}>
                {/* Image de couverture */}
                <div className="relative h-44 bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden" onClick={() => onSelect(c.course_id)}>
                  {c.image_couverture ? (
                    <img src={c.image_couverture} alt={c.titre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-slate-500/50" />
                    </div>
                  )}
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {/* Badge statut */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full backdrop-blur-sm ${c.published ? 'bg-green-500/90 text-white' : 'bg-amber-500/90 text-white'}`}>
                      {c.published ? 'Publie' : 'Brouillon'}
                    </span>
                  </div>
                  {/* Delete button */}
                  {(user.role === 'centre' || (user.role === 'formateur' && c.is_owner !== false)) && (
                    <button className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 text-white/70 hover:bg-red-500 hover:text-white opacity-0 group-hover:opacity-100 transition-all" onClick={e => { e.stopPropagation(); del(c.course_id, c.titre); }} data-testid={`delete-course-${c.course_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {/* Badge lecture seule */}
                  {user.role === 'formateur' && c.is_owner === false && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-500/90 text-white backdrop-blur-sm">Lecture seule</span>
                    </div>
                  )}
                  {/* Titre sur image */}
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-base leading-tight line-clamp-2 drop-shadow-md">{c.titre}</h3>
                  </div>
                </div>

                {/* Infos */}
                <div className="p-4">
                  <div onClick={() => onSelect(c.course_id)}>
                    {/* Formateur */}
                    {c.formateur_nom ? (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                          {c.formateur_nom.split(' ').map(n => n[0] || '').join('').substring(0, 2)}
                        </div>
                        <span className="text-sm text-slate-600 truncate">{c.formateur_nom}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-slate-400" />
                        </div>
                        <span className="text-sm text-slate-400 italic">Aucun formateur</span>
                      </div>
                    )}

                    {/* Metadonnees */}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      {c.duree_estimee && (
                        <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-md">
                          <Play className="h-3 w-3 text-slate-400" /> {c.duree_estimee}
                        </span>
                      )}
                      <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-md">
                        <BookOpen className="h-3 w-3 text-slate-400" /> {c.nb_modules || 0} modules
                      </span>
                      {c.nb_quiz > 0 && (
                        <span className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded-md text-purple-700">
                          <HelpCircle className="h-3 w-3" /> {c.nb_quiz} quiz
                        </span>
                      )}
                      {c.nb_devoirs > 0 && (
                        <span className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-md text-orange-700">
                          <FileText className="h-3 w-3" /> {c.nb_devoirs} devoir{c.nb_devoirs > 1 ? 's' : ''}
                          {c.prochaine_echeance && <span className="text-[10px] ml-1">({new Date(c.prochaine_echeance).toLocaleDateString('fr-FR')})</span>}
                        </span>
                      )}
                    </div>

                    {/* Barre de progression */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Progression moyenne</span>
                        <span className="font-semibold text-slate-700">{progression}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${progression >= 80 ? 'bg-green-500' : progression >= 40 ? 'bg-blue-500' : progression > 0 ? 'bg-amber-500' : 'bg-slate-200'}`}
                          style={{ width: `${Math.max(progression, 2)}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-100">
                    <button onClick={() => onSelect(c.course_id)} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors" data-testid={`action-stagiaires-${c.course_id}`}>
                      <Users className="h-3 w-3" /> Stagiaires ({nbInscrits})
                    </button>
                    <button onClick={() => onSelect(c.course_id)} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md transition-colors" data-testid={`action-notes-${c.course_id}`}>
                      <BarChart3 className="h-3 w-3" /> Notes
                    </button>
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try { const token = localStorage.getItem('token'); window.open(`/api/foad/courses/${c.course_id}/export-qualiopi?token=${token}`, '_blank'); } catch {}
                    }} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors" data-testid={`action-export-${c.course_id}`}>
                      <Download className="h-3 w-3" /> Export Qualiopi
                    </button>
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const r = await axios.get(`/foad/courses/${c.course_id}/certificates`);
                        setCertListData(r.data);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                    }} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors" data-testid={`action-certificat-${c.course_id}`}>
                      <Award className="h-3 w-3" /> Certificat
                    </button>
                    <button onClick={() => window.open(`/mes-formations?preview=${c.course_id}`, '_blank')} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-md transition-colors" data-testid={`action-apercu-${c.course_id}`}>
                      <Eye className="h-3 w-3" /> Apercu
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showMoodleImport && <MoodleImportModal onClose={() => setShowMoodleImport(false)} onImported={load} />}

      {/* Modal liste des certificats */}
      {certListData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCertListData(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()} data-testid="cert-list-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /> Certificats delivres</h3>
              <button onClick={() => setCertListData(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">{certListData.course_titre}</p>
            {certListData.certificates.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Award className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>Aucun certificat delivre pour ce cours</p>
              </div>
            ) : (
              <div className="space-y-2">
                {certListData.certificates.map(cert => (
                  <div key={cert.user_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-800">{cert.prenom} {cert.nom}</p>
                      <p className="text-xs text-slate-500">Score: {cert.score}% - {cert.date ? new Date(cert.date).toLocaleDateString('fr-FR') : ''}</p>
                    </div>
                    <button onClick={async () => {
                      let tamponImg = '', gerantNom = '';
                      try { const s = await axios.get('/foad/centre-stamp'); tamponImg = s.data.tampon_img || ''; gerantNom = s.data.gerant_nom || ''; } catch {}
                      const duree = certListData.duree_estimee || '';
                      setCertPreviewData({
                        prenom: cert.prenom, nom: cert.nom,
                        courseTitre: certListData.course_titre,
                        courseDescription: certListData.description || '',
                        objectifs: certListData.objectifs || '',
                        dureeEstimee: duree ? (String(duree).match(/heure|h|min/i) ? duree : duree + ' heures') : '',
                        noteSur10: (cert.score / 10).toFixed(1),
                        nbModules: certListData.nb_modules || 0,
                        dateStr: cert.date ? new Date(cert.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
                        refNumber: certListData.course_id.substring(0, 8).toUpperCase(),
                        tamponImg, gerantNom
                      });
                    }} className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-md font-medium" data-testid={`view-cert-${cert.user_id}`}>
                      Voir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {certPreviewData && <CertificateFOAD data={certPreviewData} onClose={() => setCertPreviewData(null)} />}
    </div>
  );
};

// ============= COURSE EDITOR =============
const CourseEditor = ({ courseId, onBack, user }) => {
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showStagiaires, setShowStagiaires] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [formateurs, setFormateurs] = useState([]);
  const [certPreview, setCertPreview] = useState(null);
  const [importingMbz, setImportingMbz] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiCount, setAiCount] = useState(10);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiTargetModule, setAiTargetModule] = useState(null);
  const [showResetPanel, setShowResetPanel] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [resetting, setResetting] = useState(false);
  const isNew = !courseId;
  const isOwner = user.role === 'centre' || !courseId || (course && course.is_owner !== false);

  const [form, setForm] = useState({
    titre: '', description: '', objectifs: '', duree_estimee: '',
    seuil_reussite: 75, tentatives_max: 3, published: false, image_couverture: '',
    date_ouverture: '', heure_ouverture: '', date_fermeture: '', heure_fermeture: '',
    formateur_id: '', formateur_nom: ''
  });

  // Load formateurs
  useEffect(() => { axios.get('/foad/formateurs-disponibles').then(r => setFormateurs(r.data || [])).catch(() => {}); }, []);

  const loadCourse = useCallback(async () => {
    if (!courseId) { setLoading(false); return; }
    try {
      const res = await axios.get(`/foad/courses/${courseId}`);
      setCourse(res.data);
      setForm({
        titre: res.data.titre || '', description: res.data.description || '',
        objectifs: res.data.objectifs || '', duree_estimee: res.data.duree_estimee || '',
        seuil_reussite: res.data.seuil_reussite || 75, tentatives_max: res.data.tentatives_max || 3,
        published: res.data.published || false, image_couverture: res.data.image_couverture || '',
        date_ouverture: res.data.date_ouverture || '', heure_ouverture: res.data.heure_ouverture || '',
        date_fermeture: res.data.date_fermeture || '', heure_fermeture: res.data.heure_fermeture || '',
        formateur_id: res.data.formateur_id || '', formateur_nom: res.data.formateur_nom || '',
      });
      setModules((res.data.modules || []).map(m => ({ ...m, questions: m.questions || [], _saved: true, _originalQuestions: m.questions || [] })));
    } catch { toast.error('Erreur chargement'); onBack(); }
    finally { setLoading(false); }
  }, [courseId, onBack]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const handleImageUpload = (file, idx) => {
    if (!file) return;
    setUploadingImage(idx);
    const reader = new FileReader();
    reader.onloadend = () => { updateModule(idx, 'contenu', reader.result); updateModule(idx, 'image_filename', file.name); setUploadingImage(null); toast.success('Image ajoutee'); };
    reader.readAsDataURL(file);
  };

  const handleExportQualiopi = async () => {
    try {
      const response = await axios.get(`/foad/courses/${courseId}/export-qualiopi`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FOAD_Qualiopi_${form.titre || 'cours'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Qualiopi telecharge !');
    } catch { toast.error('Erreur export'); }
  };

  const handleImportMbz = async (file) => {
    if (!file || !file.name.endsWith('.mbz')) { toast.error('Fichier .mbz attendu'); return; }
    if (!courseId) { toast.error('Sauvegardez le cours avant d\'importer'); return; }
    setImportingMbz(true);
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('filename', file.name);
        await axios.post('/foad/upload-mbz-chunk', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      }

      const res = await axios.post('/foad/finalize-mbz-import', { upload_id: uploadId, filename: file.name, course_id: courseId }, { timeout: 180000 });
      toast.success(`${res.data.stats.modules_created} modules et ${res.data.stats.questions_created} questions importes !`);
      loadCourse();
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      toast.error(detail || 'Erreur import');
    }
    finally { setImportingMbz(false); }
  };

  const handleGenerateAI = async () => {
    if (!courseId) { toast.error('Sauvegardez le cours avant de generer'); return; }
    setGeneratingAI(true);
    try {
      const payload = { course_id: courseId, count: aiCount, add_to_quiz: true };
      if (aiTargetModule) payload.module_id = aiTargetModule;
      const res = await axios.post('/foad/question-bank/generate-ai', payload);
      toast.success(res.data.message);
      setShowAIModal(false);
      loadCourse();
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      toast.error(detail || 'Erreur generation IA');
    }
    finally { setGeneratingAI(false); }
  };

  // ============= BACKUP & RESET FUNCTIONS =============
  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const r = await axios.get(`/foad/courses/${courseId}/backups`);
      setBackups(r.data || []);
    } catch { toast.error('Erreur chargement sauvegardes'); }
    finally { setLoadingBackups(false); }
  };

  const handleBackup = async () => {
    try {
      await axios.post(`/foad/courses/${courseId}/backup`);
      toast.success('Sauvegarde créée avec succès');
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur sauvegarde'); }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir remettre à zéro TOUTES les progressions de ce cours ?\n\nCette action est irréversible (pensez à faire une sauvegarde avant).')) return;
    setResetting(true);
    try {
      const r = await axios.post(`/foad/courses/${courseId}/reset`);
      toast.success(r.data.message);
      loadCourse();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur remise à zéro'); }
    finally { setResetting(false); }
  };

  const handleResetUser = async (userId, userName) => {
    if (!window.confirm(`Remettre à zéro la progression de ${userName} ?`)) return;
    try {
      await axios.post(`/foad/courses/${courseId}/reset/${userId}`);
      toast.success(`Progression de ${userName} réinitialisée`);
      loadCourse();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur remise à zéro'); }
  };

  const handleRestore = async (backupId, date) => {
    if (!window.confirm(`Restaurer la sauvegarde du ${date} ?\n\nCela remplacera toutes les progressions actuelles.`)) return;
    try {
      const r = await axios.post(`/foad/courses/${courseId}/restore/${backupId}`);
      toast.success(r.data.message);
      loadCourse();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur restauration'); }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Supprimer cette sauvegarde ?')) return;
    try {
      await axios.delete(`/foad/courses/${courseId}/backups/${backupId}`);
      toast.success('Sauvegarde supprimée');
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  const saveCourse = async () => {
    if (!form.titre.trim()) { toast.error('Titre obligatoire'); return; }
    setSaving(true);
    try {
      let cId = courseId;
      if (isNew) { const r = await axios.post('/foad/courses', form); cId = r.data.course_id; toast.success('Cours cree'); }
      else { await axios.put(`/foad/courses/${courseId}`, form); }

      for (let i = 0; i < modules.length; i++) {
        const m = modules[i];
        const d = {
          titre: m.titre, type: m.type, contenu: m.contenu || '', video_url: m.video_url || '',
          description: m.description || '', obligatoire: m.obligatoire !== false, bloquant: m.bloquant || false,
          ordre: i, image_url: m.image_url || '', image_filename: m.image_filename || '',
          embed_code: m.embed_code || '', note_passage: m.note_passage ?? 5.0,
          tentatives_quiz: m.tentatives_quiz ?? 3,
          due_date: m.due_date || null, end_date: m.end_date || null,
          date_ouverture: m.date_ouverture || null, heure_ouverture: m.heure_ouverture || null,
          document_access: m.document_access || 'consulter'
        };
        let mId = m.module_id;
        if (m.module_id?.startsWith('new_')) { const r = await axios.post(`/foad/courses/${cId}/modules`, d); mId = r.data.module_id; }
        else { await axios.put(`/foad/courses/${cId}/modules/${m.module_id}`, d); }

        if (m.type === 'quiz') {
          if (!m.module_id?.startsWith('new_')) { for (const q of (m._originalQuestions || [])) { try { await axios.delete(`/foad/questions/${q.question_id}`); } catch {} } }
          for (const q of (m.questions || [])) {
            await axios.post(`/foad/modules/${mId}/questions`, {
              texte: q.texte, type: q.type, options: q.options, points: q.points || 1,
              bareme_detail: q.bareme_detail || '', explication: q.explication || '',
              obligatoire: q.obligatoire !== false, pairs: q.pairs || [], blanks: q.blanks || []
            });
          }
        }
      }
      toast.success('Sauvegarde !'); onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const addModule = (type) => {
    setModules(p => [...p, {
      module_id: `new_${Date.now()}`, titre: '', type, contenu: '', video_url: '', description: '',
      obligatoire: true, bloquant: false, questions: [], ordre: p.length,
      image_url: '', image_filename: '', embed_code: '', note_passage: 5.0, tentatives_quiz: 3,
      date_ouverture: '', heure_ouverture: '', document_access: 'consulter'
    }]);
    setExpandedModule(modules.length);
  };
  const updateModule = (i, f, v) => { setModules(p => { const u = [...p]; u[i] = { ...u[i], [f]: v }; return u; }); };
  const removeModule = async (i) => { const m = modules[i]; if (!m.module_id?.startsWith('new_') && courseId) { try { await axios.delete(`/foad/courses/${courseId}/modules/${m.module_id}`); } catch {} } setModules(p => p.filter((_, j) => j !== i)); };
  const moveModule = (i, d) => { const n = i + d; if (n < 0 || n >= modules.length) return; setModules(p => { const u = [...p]; [u[i], u[n]] = [u[n], u[i]]; return u; }); };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-4xl mx-auto">
      {showFullscreen && <FullScreenPreview course={form} modules={modules} onClose={() => setShowFullscreen(false)} />}
      {certPreview && <CertificateFOAD data={certPreview} onClose={() => setCertPreview(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="foad-back"><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="text-xl font-bold text-slate-800">{isNew ? 'Nouveau cours' : isOwner ? 'Modifier le cours' : 'Consulter le cours'}</h1>
          {!isOwner && !isNew && <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">Lecture seule</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowStagiaires(!showStagiaires)} className={showStagiaires ? 'bg-blue-50 border-blue-300' : ''} data-testid="toggle-stagiaires">
                <Users className="h-4 w-4 mr-1" /> Stagiaires {course?.stagiaires_inscrits?.length > 0 && `(${course.stagiaires_inscrits.length})`}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowScores(!showScores)} className={showScores ? 'bg-purple-50 border-purple-300' : ''} data-testid="toggle-scores">
                <BarChart3 className="h-4 w-4 mr-1" /> Notes
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportQualiopi} data-testid="export-qualiopi">
                <Download className="h-4 w-4 mr-1" /> Export Qualiopi
              </Button>
              <Button variant="outline" size="sm" onClick={async () => {
                let tamponImg = '', gerantNom = '';
                try { const s = await axios.get('/foad/centre-stamp'); tamponImg = s.data.tampon_img || ''; gerantNom = s.data.gerant_nom || ''; } catch {}
                setCertPreview({
                  prenom: 'Apercu', nom: 'Certificat',
                  courseTitre: form.titre, courseDescription: form.description,
                  objectifs: form.objectifs, dureeEstimee: form.duree_estimee,
                  noteSur10: '8.5', nbModules: modules.length,
                  dateStr: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
                  refNumber: courseId ? courseId.substring(0, 8).toUpperCase() : 'PREVIEW',
                  tamponImg, gerantNom
                });
              }} className="border-green-300 text-green-700 hover:bg-green-50" data-testid="cert-preview-btn">
                <Award className="h-4 w-4 mr-1" /> Certificat
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowFullscreen(true)} data-testid="foad-fullscreen">
                <Maximize className="h-4 w-4 mr-1" /> Apercu
              </Button>
              {isOwner && (
                <Button variant="outline" size="sm" onClick={() => { setShowResetPanel(!showResetPanel); if (!showResetPanel) loadBackups(); }}
                  className={`${showResetPanel ? 'bg-amber-50 border-amber-300' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`} data-testid="toggle-reset-panel">
                  <Archive className="h-4 w-4 mr-1" /> Gestion
                </Button>
              )}
            </>
          )}
          {isOwner && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              {form.published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
              <button type="button" onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.published ? 'bg-green-500' : 'bg-slate-300'}`}
                data-testid="toggle-published">
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${form.published ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={form.published ? 'text-green-600 font-medium' : 'text-slate-400'}>{form.published ? 'Publie' : 'Brouillon'}</span>
            </label>
          )}
          {isOwner && (
            <Button onClick={saveCourse} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="foad-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          )}
        </div>
      </div>

      {/* Panels */}
      {showStagiaires && !isNew && <StagiairesPanel courseId={courseId} inscrits={course?.stagiaires_inscrits} onUpdate={loadCourse} />}
      {showScores && !isNew && <QuizScoresDashboard courseId={courseId} courseData={course} />}

      {/* Backup & Reset Panel */}
      {showResetPanel && !isNew && (
        <div className="bg-white rounded-xl border p-6 mb-6" data-testid="reset-panel">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Archive className="h-5 w-5 text-amber-600" /> Sauvegarde & Remise à zéro
          </h3>

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <button onClick={handleBackup}
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
              data-testid="backup-btn">
              <Database className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-800 text-sm">Sauvegarder</p>
                <p className="text-xs text-blue-600">Créer un point de restauration</p>
              </div>
            </button>
            <button onClick={handleResetAll} disabled={resetting}
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-red-200 bg-red-50 hover:bg-red-100 transition-colors text-left"
              data-testid="reset-all-btn">
              <RotateCcw className={`h-6 w-6 text-red-600 flex-shrink-0 ${resetting ? 'animate-spin' : ''}`} />
              <div>
                <p className="font-semibold text-red-800 text-sm">Tout réinitialiser</p>
                <p className="text-xs text-red-600">Remettre à zéro tous les stagiaires</p>
              </div>
            </button>
            <button onClick={() => { setShowResetPanel(false); setShowStagiaires(true); }}
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
              data-testid="reset-individual-btn">
              <User className="h-6 w-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Par stagiaire</p>
                <p className="text-xs text-amber-600">Réinitialiser un stagiaire</p>
              </div>
            </button>
          </div>

          {/* Stagiaires individuels - reset */}
          {course?.stagiaires_inscrits?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Users className="h-4 w-4" /> Réinitialiser un stagiaire
              </h4>
              <div className="border rounded-lg divide-y max-h-48 overflow-auto">
                {course.stagiaires_inscrits.map(s => (
                  <div key={s.user_id} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                    <span className="text-sm text-slate-700">{s.prenom} {s.nom}</span>
                    <button onClick={() => handleResetUser(s.user_id, `${s.prenom} ${s.nom}`)}
                      className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
                      data-testid={`reset-user-${s.user_id}`}>
                      <RotateCcw className="h-3 w-3" /> Réinitialiser
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Backups list */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Clock className="h-4 w-4" /> Sauvegardes ({backups.length})
            </h4>
            {loadingBackups ? (
              <div className="text-center py-4 text-slate-400 text-sm">Chargement...</div>
            ) : backups.length === 0 ? (
              <div className="text-center py-4 text-slate-400 text-sm bg-slate-50 rounded-lg">Aucune sauvegarde</div>
            ) : (
              <div className="border rounded-lg divide-y max-h-60 overflow-auto">
                {backups.map(b => (
                  <div key={b.backup_id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {new Date(b.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-xs text-slate-500">{b.nb_stagiaires} stagiaire(s) - par {b.created_by_nom}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleRestore(b.backup_id, new Date(b.created_at).toLocaleDateString('fr-FR'))}
                        className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md font-medium"
                        data-testid={`restore-${b.backup_id}`}>
                        Restaurer
                      </button>
                      <button onClick={() => handleDeleteBackup(b.backup_id)}
                        className="text-xs px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-md"
                        data-testid={`delete-backup-${b.backup_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              La remise à zéro supprime toutes les progressions, les tentatives de quiz et les certificats des stagiaires.
              Pensez à créer une sauvegarde avant de réinitialiser.
            </p>
          </div>
        </div>
      )}

      {/* Course Info */}
      <div className="bg-white rounded-xl border p-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className="text-sm font-medium text-slate-700 mb-1 block">Titre *</label><input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex: Anatomie et mouvement" className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="foad-course-title" disabled={!isOwner} /></div>
          <div className="col-span-2"><label className="text-sm font-medium text-slate-700 mb-1 block">Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full border rounded-lg px-3 py-2.5 text-sm" disabled={!isOwner} /></div>
          <div className="col-span-2"><label className="text-sm font-medium text-slate-700 mb-1 block">Objectifs</label><textarea value={form.objectifs} onChange={e => setForm(f => ({ ...f, objectifs: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2.5 text-sm" disabled={!isOwner} /></div>

          {/* Formateur */}
          <div className="col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block flex items-center gap-2"><User className="h-4 w-4 text-blue-500" /> Formateur assigne</label>
            <select value={form.formateur_id || ''} onChange={e => {
              const fId = e.target.value;
              const f = formateurs.find(f => f.user_id === fId);
              setForm(prev => ({ ...prev, formateur_id: fId, formateur_nom: f ? `${f.prenom} ${f.nom}` : '' }));
            }} className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="foad-formateur-select">
              <option value="">-- Aucun formateur --</option>
              {formateurs.map(f => (<option key={f.user_id} value={f.user_id}>{f.prenom} {f.nom} ({f.email})</option>))}
            </select>
          </div>

          {/* Image couverture */}
          <div className="col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Image de couverture</label>
            {form.image_couverture ? (
              <div className="relative w-40 h-24 rounded-lg overflow-hidden border"><img src={form.image_couverture} alt="" className="w-full h-full object-cover" /><button onClick={() => setForm(f => ({ ...f, image_couverture: '' }))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X className="h-3 w-3" /></button></div>
            ) : (
              <div className="relative"><input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onloadend = () => setForm(p => ({ ...p, image_couverture: r.result })); r.readAsDataURL(f); } }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" /><div className="w-40 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 cursor-pointer"><Image className="h-6 w-6 mb-1" /><span className="text-xs">Ajouter</span></div></div>
            )}
          </div>
          <div><label className="text-sm font-medium text-slate-700 mb-1 block">Duree estimee</label><input value={form.duree_estimee} onChange={e => setForm(f => ({ ...f, duree_estimee: e.target.value }))} placeholder="Ex: 2h30" className="w-full border rounded-lg px-3 py-2.5 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1 block">Seuil reussite (%)</label><input type="number" min={0} max={100} value={form.seuil_reussite} onChange={e => setForm(f => ({ ...f, seuil_reussite: parseInt(e.target.value) || 75 }))} className="w-full border rounded-lg px-3 py-2.5 text-sm" /></div>
          <div><label className="text-sm font-medium text-slate-700 mb-1 block">Tentatives max (global)</label><input type="number" min={1} max={99} value={form.tentatives_max} onChange={e => setForm(f => ({ ...f, tentatives_max: parseInt(e.target.value) || 3 }))} className="w-full border rounded-lg px-3 py-2.5 text-sm" /></div>
        </div>
        {/* Planification */}
        <div className="mt-6 pt-6 border-t">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Settings className="h-4 w-4 text-slate-500" /> Planification</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Date ouverture</label><input type="date" value={form.date_ouverture} onChange={e => setForm(f => ({ ...f, date_ouverture: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="foad-date-ouverture" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Heure ouverture</label><input type="time" value={form.heure_ouverture} onChange={e => setForm(f => ({ ...f, heure_ouverture: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Date fermeture</label><input type="date" value={form.date_fermeture} onChange={e => setForm(f => ({ ...f, date_fermeture: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="foad-date-fermeture" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Heure fermeture</label><input type="time" value={form.heure_fermeture} onChange={e => setForm(f => ({ ...f, heure_fermeture: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
          </div>
          <p className="text-xs text-slate-400 mt-2">Laissez vide pour un cours sans limite de temps.</p>
        </div>
      </div>

      {/* Modules */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Modules du parcours</h2>
        <div className="space-y-3">
          {modules.map((m, idx) => {
            const cfg = moduleTypeConfig[m.type] || moduleTypeConfig.texte;
            const Icon = cfg.icon;
            const isExp = expandedModule === idx;
            const isCh = m.type === 'chapitre';
            const isQuiz = m.type === 'quiz';
            const isDevoir = m.type === 'devoir';
            return (
              <div key={m.module_id} className={`bg-white rounded-xl border overflow-hidden ${isCh ? 'border-slate-400 shadow-sm' : 'border-slate-200'}`} data-testid={`module-${idx}`}>
                <div className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 ${isCh ? 'bg-slate-100' : ''}`} onClick={() => setExpandedModule(isExp ? null : idx)}>
                  <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  <div className="flex items-center gap-1">
                    {isOwner && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); moveModule(idx, -1); }} disabled={idx === 0}><ChevronUp className="h-3 w-3" /></Button>}
                    {isOwner && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); moveModule(idx, 1); }} disabled={idx === modules.length - 1}><ChevronDown className="h-3 w-3" /></Button>}
                  </div>
                  <span className={`p-1.5 rounded ${cfg.color}`}><Icon className="h-4 w-4" /></span>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${isCh ? 'text-slate-900 text-base font-bold uppercase tracking-wide' : 'text-slate-800'}`}>{m.titre || cfg.label}</span>
                    {m.bloquant && <span className="ml-2 text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded"><Lock className="h-3 w-3 inline mr-0.5" />Bloquant</span>}
                    {m.date_ouverture && <span className="ml-1 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded"><Calendar className="h-3 w-3 inline mr-0.5" />{new Date(m.date_ouverture).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  {isOwner && <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={e => { e.stopPropagation(); removeModule(idx); }}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  {isExp ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>

                {isExp && (
                  <div className="border-t p-4 bg-slate-50/50 space-y-4">
                    {isOwner ? (
                      <>
                    <div><label className="text-sm font-medium text-slate-700 mb-1 block">{isCh ? 'Titre du chapitre' : 'Titre du module'}</label><input value={m.titre} onChange={e => updateModule(idx, 'titre', e.target.value)} placeholder="Titre..." className="w-full border rounded-lg px-3 py-2 text-sm" /></div>

                    {/* DATE D'OUVERTURE DU MODULE */}
                    {!isCh && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                        <Calendar className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-xs font-medium text-blue-700">Ouverture :</label>
                          <input type="date" value={m.date_ouverture || ''} onChange={e => updateModule(idx, 'date_ouverture', e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid={`module-date-ouverture-${idx}`} />
                          <input type="time" value={m.heure_ouverture || ''} onChange={e => updateModule(idx, 'heure_ouverture', e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid={`module-heure-ouverture-${idx}`} />
                          {m.date_ouverture && <button onClick={() => { updateModule(idx, 'date_ouverture', ''); updateModule(idx, 'heure_ouverture', ''); }} className="text-xs text-red-500 hover:underline">Effacer</button>}
                        </div>
                        {!m.date_ouverture && <span className="text-xs text-slate-400 italic">Accessible immediatement</span>}
                      </div>
                    )}

                    {/* CHAPITRE */}
                    {isCh && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Image du chapitre</label>
                          {m.contenu ? (
                            <div className="relative inline-block"><img src={m.contenu} alt="" className="max-h-40 rounded-lg border" /><button onClick={() => updateModule(idx, 'contenu', '')} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"><X className="h-4 w-4" /></button></div>
                          ) : (
                            <div className="relative"><input type="file" accept="image/*" onChange={e => handleImageUpload(e.target.files[0], idx)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" data-testid={`chapitre-image-${idx}`} /><div className="w-48 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 cursor-pointer"><Image className="h-6 w-6 mb-1" /><span className="text-xs">Ajouter photo</span></div></div>
                          )}
                        </div>
                        <div><label className="text-sm font-medium text-slate-700 mb-1 block">Description</label><textarea value={m.description || ''} onChange={e => updateModule(idx, 'description', e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                      </>
                    )}

                    {/* TEXTE */}
                    {m.type === 'texte' && <div><label className="text-sm font-medium text-slate-700 mb-1 block">Contenu</label><RichTextEditor value={m.contenu || ''} onChange={v => updateModule(idx, 'contenu', v)} placeholder="Redigez le contenu..." /></div>}

                    {/* IMAGE */}
                    {m.type === 'image' && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Image</label>
                        {m.contenu ? (
                          <div><div className="relative inline-block"><img src={m.contenu} alt="" className="max-h-64 rounded-lg border shadow-sm" /><button onClick={() => { updateModule(idx, 'contenu', ''); updateModule(idx, 'image_filename', ''); }} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"><X className="h-4 w-4" /></button></div><div className="mt-2"><label className="text-xs text-slate-600 mb-1 block">Legende</label><input value={m.description || ''} onChange={e => updateModule(idx, 'description', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></div></div>
                        ) : (
                          <div className="relative"><input type="file" accept="image/*" onChange={e => handleImageUpload(e.target.files[0], idx)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" data-testid={`image-upload-${idx}`} /><div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 cursor-pointer">{uploadingImage === idx ? <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /> : <><Image className="h-10 w-10 text-slate-400 mx-auto mb-3" /><p className="text-slate-600 font-medium">Cliquez ou glissez une image</p></>}</div></div>
                        )}
                      </div>
                    )}

                    {/* VIDEO */}
                    {m.type === 'video' && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">URL YouTube</label>
                        <input value={m.video_url || ''} onChange={e => updateModule(idx, 'video_url', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="w-full border rounded-lg px-3 py-2 text-sm" />
                        {(() => { const match = m.video_url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?\s]+)/); return match ? <div className="mt-3 rounded-lg overflow-hidden aspect-video"><iframe src={`https://www.youtube.com/embed/${match[1]}`} className="w-full h-full" allowFullScreen title={m.titre} /></div> : null; })()}
                      </div>
                    )}

                    {/* GOOGLE SLIDES */}
                    {m.type === 'google_slides' && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Code d'integration Google Slides</label>
                        <p className="text-xs text-slate-500 mb-2">Copiez le code depuis Google Slides: Fichier &gt; Publier sur le Web &gt; Integrer</p>
                        <textarea value={m.embed_code || ''} onChange={e => updateModule(idx, 'embed_code', e.target.value)} rows={4} placeholder='<iframe src="https://docs.google.com/presentation/d/..." ...' className="w-full border rounded-lg px-3 py-2 text-sm font-mono" data-testid={`gslides-embed-${idx}`} />
                        {m.embed_code && (<div className="mt-3 rounded-lg overflow-hidden border" style={{ position: 'relative', paddingBottom: '60%', height: 0 }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(m.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"').replace(/<iframe/g, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"')) }} />)}
                      </div>
                    )}

                    {/* GOOGLE DOCS / SHEETS */}
                    {m.type === 'google_docs' && (
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Integration Google Docs / Sheets</label>
                        <p className="text-xs text-slate-500 mb-2">Collez l'URL publique ou le code embed. Pour Google Docs : Fichier &gt; Partager &gt; Publier sur le Web. Pour Google Sheets : Fichier &gt; Partager &gt; Publier sur le Web.</p>
                        <input type="text" value={m.contenu || ''} onChange={e => updateModule(idx, 'contenu', e.target.value)} placeholder="https://docs.google.com/document/d/... ou https://docs.google.com/spreadsheets/d/..." className="w-full border rounded-lg px-3 py-2 text-sm mb-2" data-testid={`gdoc-url-${idx}`} />
                        <p className="text-xs text-slate-400 mb-2">Ou collez le code embed complet :</p>
                        <textarea value={m.embed_code || ''} onChange={e => updateModule(idx, 'embed_code', e.target.value)} rows={3} placeholder='<iframe src="https://docs.google.com/document/d/.../pub?embedded=true" ...' className="w-full border rounded-lg px-3 py-2 text-sm font-mono" data-testid={`gdoc-embed-${idx}`} />
                        {(m.contenu || m.embed_code) && (
                          <div className="mt-3 rounded-lg overflow-hidden border" style={{ height: '400px' }}>
                            {m.embed_code ? (
                              <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: nettoyerHtml(m.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"')) }} />
                            ) : m.contenu ? (
                              <iframe src={m.contenu.includes('/pub') ? m.contenu : m.contenu.replace(/\/edit.*$/, '/pub?embedded=true')} className="w-full h-full border-0" title={m.titre || 'Google Document'} />
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}

                    {/* DOCUMENT - Upload ou Embed */}
                    {m.type === 'document' && (
                      <div className="space-y-3">
                        {/* Option 1: Upload direct */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <label className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2"><Upload className="h-4 w-4" /> Uploader un fichier (max 5 Mo)</label>
                          {m.contenu && m.contenu.startsWith('doc:') ? (
                            <div className="flex items-center gap-3 mt-2 bg-white p-3 rounded-lg border">
                              <FileText className="h-5 w-5 text-amber-600 flex-shrink-0" />
                              <span className="text-sm font-medium truncate flex-1">{m.description || 'Document uploade'}</span>
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/foad/documents/${m.contenu.replace('doc:', '')}?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Voir</a>
                              <button type="button" onClick={() => { updateModule(idx, 'contenu', ''); updateModule(idx, 'description', ''); }} className="text-xs text-red-500 hover:underline">Supprimer</button>
                            </div>
                          ) : (
                            <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.txt,.rtf" data-testid={`doc-upload-${idx}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const res = await axios.post('/foad/upload-document', formData);
                                  updateModule(idx, 'contenu', `doc:${res.data.doc_id}`);
                                  updateModule(idx, 'description', file.name);
                                  toast.success('Document uploade');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
                              }}
                              className="mt-2 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200 cursor-pointer"
                            />
                          )}
                          <p className="text-xs text-slate-400 mt-1">PDF, Word, PowerPoint, Excel, ODT, TXT (max 5 Mo)</p>
                        </div>
                        {/* Droits d'acces */}
                        {m.contenu && m.contenu.startsWith('doc:') && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <label className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Lock className="h-4 w-4" /> Droits d'acces du stagiaire</label>
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              {[
                                { val: 'consulter', label: 'Consulter uniquement', desc: 'Le stagiaire peut lire mais pas telecharger', icon: '👁' },
                                { val: 'telecharger', label: 'Telecharger uniquement', desc: 'Le stagiaire peut telecharger le fichier', icon: '⬇' },
                                { val: 'les_deux', label: 'Consulter + Telecharger', desc: 'Le stagiaire peut lire et telecharger', icon: '👁⬇' },
                              ].map(opt => (
                                <button key={opt.val} type="button"
                                  onClick={() => updateModule(idx, 'document_access', opt.val)}
                                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${(m.document_access || 'consulter') === opt.val ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                                  data-testid={`doc-access-${opt.val}-${idx}`}
                                >
                                  <span className="text-sm font-medium block">{opt.label}</span>
                                  <span className="text-xs text-slate-500">{opt.desc}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Option 2: Embed Google */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <label className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2"><Image className="h-4 w-4" /> Ou integrer via Google (code embed)</label>
                          <textarea value={m.embed_code || ''} onChange={e => updateModule(idx, 'embed_code', e.target.value)} rows={3} placeholder='<iframe src="https://docs.google.com/document/d/..." ...' className="w-full border rounded-lg px-3 py-2 text-sm font-mono mt-2" data-testid={`doc-embed-${idx}`} />
                          {m.embed_code && (<div className="mt-3 rounded-lg overflow-hidden border" style={{ height: '400px' }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(m.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"')) }} />)}
                        </div>
                      </div>
                    )}

                    {/* DEVOIR */}
                    {isDevoir && (
                      <div className="space-y-4">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-2 mb-2"><PenLine className="h-4 w-4" /> Configuration du devoir</h4>
                          <p className="text-xs text-orange-600 mb-3">Le formateur propose un sujet de devoir. Les stagiaires pourront deposer leur travail (fichier Word, PDF). Le formateur pourra ensuite corriger et noter chaque devoir.</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Sujet / Consignes du devoir</label>
                          <RichTextEditor value={m.contenu || ''} onChange={v => updateModule(idx, 'contenu', v)} placeholder="Decrivez le devoir, les consignes, les criteres d'evaluation..." />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Date de rendu attendue</label>
                            <input type="date" value={m.due_date || ''} onChange={e => updateModule(idx, 'due_date', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid={`devoir-due-date-${idx}`} />
                            <p className="text-xs text-slate-400 mt-1">Date indicative pour les stagiaires</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Date limite de remise</label>
                            <input type="date" value={m.end_date || ''} onChange={e => updateModule(idx, 'end_date', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid={`devoir-end-date-${idx}`} />
                            <p className="text-xs text-slate-400 mt-1">Apres cette date, plus de depot possible</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block">Note maximale (/10)</label>
                            <input type="number" min={1} max={10} step={0.5} value={m.note_passage ?? 10} onChange={e => updateModule(idx, 'note_passage', parseFloat(e.target.value) || 10)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid={`devoir-note-max-${idx}`} />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Formats acceptes</label>
                          <p className="text-xs text-slate-500">PDF (.pdf), Word (.docx), ODT, TXT, RTF (max 20 MB)</p>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer text-orange-700">
                          <input type="checkbox" checked={m.bloquant || false} onChange={e => updateModule(idx, 'bloquant', e.target.checked)} className="rounded" />
                          {m.bloquant ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                          Devoir bloquant (doit etre rendu pour continuer)
                        </label>
                        {/* Submissions panel for existing modules */}
                        {m.module_id && !m.module_id.startsWith('new_') && (
                          <DevoirSubmissionsPanel courseId={course.course_id} moduleId={m.module_id} noteMax={m.note_passage || 10} />
                        )}
                      </div>
                    )}

                    {/* QUIZ */}
                    {isQuiz && (
                      <>
                        {/* Quiz settings */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-purple-700 flex items-center gap-2"><Settings className="h-4 w-4" /> Parametres du quiz</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-purple-600 mb-1 block">Note de passage (/10)</label>
                              <input type="number" min={0} max={10} step={0.5} value={m.note_passage ?? 5} onChange={e => updateModule(idx, 'note_passage', parseFloat(e.target.value) || 5)} className="w-full border rounded-lg px-3 py-1.5 text-sm" data-testid={`quiz-note-passage-${idx}`} />
                            </div>
                            <div>
                              <label className="text-xs text-purple-600 mb-1 block">Tentatives max</label>
                              <input type="number" min={1} max={99} value={m.tentatives_quiz ?? 3} onChange={e => updateModule(idx, 'tentatives_quiz', parseInt(e.target.value) || 3)} className="w-full border rounded-lg px-3 py-1.5 text-sm" data-testid={`quiz-tentatives-${idx}`} />
                            </div>
                          </div>
                          <label className="flex items-center gap-2 text-sm cursor-pointer text-purple-700">
                            <input type="checkbox" checked={m.bloquant || false} onChange={e => updateModule(idx, 'bloquant', e.target.checked)} className="rounded" />
                            {m.bloquant ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                            Quiz bloquant (doit etre reussi pour continuer)
                          </label>
                        </div>
                        <div><label className="text-sm font-medium text-slate-700 mb-2 block">Questions</label><QuestionEditor questions={m.questions || []} onChange={qs => updateModule(idx, 'questions', qs)} moduleId={m.module_id} /></div>
                      </>
                    )}
                      </>
                    ) : (
                      /* MODE LECTURE SEULE pour formateur non-propriétaire */
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <span className="font-medium">{m.titre || cfg.label}</span>
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">{cfg.label}</span>
                        </div>
                        {m.contenu && m.type === 'texte' && <div className="prose prose-sm max-w-none text-slate-600" dangerouslySetInnerHTML={{ __html: nettoyerHtml(m.contenu) }} />}
                        {m.contenu && m.type === 'image' && <img src={m.contenu} alt="" className="max-h-48 rounded-lg" />}
                        {m.contenu && m.type === 'video' && <p className="text-sm text-slate-500">Video: {m.titre}</p>}
                        {isQuiz && <p className="text-sm text-slate-500">{(m.questions || []).length} question(s) - Note passage: {m.note_passage ?? 5}/10</p>}
                        {m.type === 'devoir' && <p className="text-sm text-slate-500">Devoir{m.bloquant ? ' (bloquant)' : ''}</p>}
                        {m.description && <p className="text-xs text-slate-400 whitespace-pre-line">{m.description}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isOwner && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ajouter un element</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(moduleTypeConfig).map(([type, cfg]) => {
              const I = cfg.icon;
              return <Button key={type} variant="outline" size="sm" onClick={() => addModule(type)} className={`text-slate-600 ${type === 'chapitre' ? 'border-slate-400 font-semibold' : ''}`} data-testid={`add-module-${type}`}><I className="h-3.5 w-3.5 mr-1.5" /> {cfg.label}</Button>;
            })}
          </div>

          {/* Import Moodle MBZ */}
          {!isNew && (
            <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
              <p className="text-xs font-semibold text-orange-500 uppercase mb-2">Importer depuis Moodle</p>
              <div className="relative inline-block">
                <input type="file" accept=".mbz" onChange={e => { if (e.target.files[0]) handleImportMbz(e.target.files[0]); e.target.value = ''; }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" data-testid="import-mbz-modules" disabled={importingMbz} />
                <Button variant="outline" size="sm" className="border-orange-300 text-orange-600 hover:bg-orange-50" disabled={importingMbz} data-testid="import-mbz-btn">
                  {importingMbz ? (
                    <><div className="animate-spin h-3.5 w-3.5 border-2 border-orange-500 border-t-transparent rounded-full mr-2" /> Import en cours...</>
                  ) : (
                    <><Download className="h-3.5 w-3.5 mr-1.5" /> Importer .mbz (chapitres, quiz, images)</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Les modules du fichier Moodle seront ajoutes a la suite du cours actuel.</p>
            </div>
          )}

          {/* AI Question Generation */}
          {!isNew && (
            <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
              <p className="text-xs font-semibold text-violet-500 uppercase mb-2">Generation IA</p>
              <Button variant="outline" size="sm" className="border-violet-300 text-violet-600 hover:bg-violet-50"
                onClick={() => { setAiTargetModule(null); setShowAIModal(true); }} data-testid="generate-ai-course-btn">
                <Zap className="h-3.5 w-3.5 mr-1.5" /> Generer des questions IA (tout le cours)
              </Button>
              {modules.filter(m => m.type === 'chapitre').length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {modules.filter(m => m.type === 'chapitre').map(ch => (
                    <button key={ch.module_id} onClick={() => { setAiTargetModule(ch.module_id); setShowAIModal(true); }}
                      className="text-xs px-2 py-1 bg-violet-50 text-violet-600 rounded-md hover:bg-violet-100 transition-colors" data-testid={`generate-ai-chapter-${ch.module_id}`}>
                      <Zap className="h-3 w-3 inline mr-1" />{ch.titre}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI Modal */}
          {showAIModal && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowAIModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="ai-modal">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
                  <Zap className="h-5 w-5 text-violet-600" /> Generation IA
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {aiTargetModule ? `Generer des questions pour le chapitre selectionne` : `Generer des questions pour tout le cours`}
                </p>
                <div className="mb-4">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Nombre de questions</label>
                  <div className="flex gap-2">
                    {[5, 10, 15].map(n => (
                      <button key={n} onClick={() => setAiCount(n)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${aiCount === n ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`ai-count-${n}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-4 bg-slate-50 rounded-lg p-2.5">
                  Les questions seront ajoutees dans la <strong>Banque de Questions</strong> et comme <strong>module quiz</strong> dans ce cours.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowAIModal(false)} data-testid="ai-cancel">Annuler</Button>
                  <Button onClick={handleGenerateAI} disabled={generatingAI} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="ai-generate-btn">
                    {generatingAI ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Generation...</> : <><Zap className="h-4 w-4 mr-2" /> Generer</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
      {/* Floating save button */}
      {isOwner && (
      <div className="fixed bottom-6 right-6 z-40">
        <Button onClick={saveCourse} disabled={saving} className="bg-blue-600 hover:bg-blue-700 shadow-xl shadow-blue-600/25 px-6 py-3 text-sm font-bold rounded-xl" data-testid="foad-save-floating">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </Button>
      </div>
      )}
    </div>
  );
};

// ============= MAIN =============
const FOADPage = ({ user }) => {
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  if (view === 'edit') return <CourseEditor courseId={selectedId} onBack={() => { setView('list'); setSelectedId(null); }} user={user} />;
  return <CourseList user={user} onSelect={id => { setSelectedId(id); setView('edit'); }} onNew={() => { setSelectedId(null); setView('edit'); }} />;
};

export default FOADPage;
